'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, setAccessToken, beginInitialRefresh, resolveInitialRefresh } from '@/lib/api-client';

export interface TwoFactorRequired {
  requiresTwoFactor: true;
  tempToken: string;
}

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  tenantId: string;
  tenantSlug?: string;
  tenant?: { slug?: string };
  language?: string | null;
  timezone?: string;
  phone?: string;
  avatarUrl?: string;
  isPlatformOwner: boolean;
  roleName?: string | null;
  planFeatures?: string[];
  planName?: string;
}

export interface CompanyOption {
  id: string;
  name: string;
  isDefault: boolean;
  country?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void | TwoFactorRequired>;
  logout: () => Promise<void>;
  register: (data: Record<string, unknown>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  companies: CompanyOption[];
  activeCompanyId: string | null;
  activeCompanyName: string | null;
  activeCompany: CompanyOption | null;
  companyCountry: string;
  setActiveCompany: (companyId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Compute isPlatformOwner: isAdmin on the default tenant
function withPlatformOwner(raw: Omit<AuthUser, 'isPlatformOwner'> & { isPlatformOwner?: boolean }): AuthUser {
  const slug = raw.tenantSlug || raw.tenant?.slug;
  return {
    ...raw,
    isPlatformOwner: !!(raw.isAdmin && slug === 'default'),
  };
}

function resolveActiveCompany(companies: CompanyOption[], storedId: string | null): string | null {
  if (!companies.length) return null;
  // When there is exactly 1 company, always auto-select it — no user interaction needed.
  if (companies.length === 1) return companies[0].id;
  if (storedId && companies.find((c) => c.id === storedId)) return storedId;
  const def = companies.find((c) => c.isDefault);
  return def ? def.id : companies[0].id;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    // Hydrate activeCompanyId from localStorage on client only (SSR-safe, prevents React #418)
    const stored = localStorage.getItem('activeCompanyId');
    if (stored) setActiveCompanyId(stored);
  }, []);

  useEffect(() => {
    // SECURITY: accessToken is no longer persisted in localStorage (XSS protection).
    // refreshToken is stored in an HttpOnly cookie (set by the API) — inaccessible to JavaScript.
    // On page load, attempt a silent token refresh; the browser sends the cookie automatically.
    // If the user record exists in localStorage we optimistically attempt the refresh.
    const userData = localStorage.getItem('user');

    // Guard against legacy stale values: older clients persisted `undefined` as a
    // literal string, which JSON.parse would then crash on. Treat as absent.
    if (userData && userData !== 'undefined' && userData !== 'null') {
      try {
        const parsed = JSON.parse(userData);

        // Attempt silent refresh to restore access token in memory.
        // beginInitialRefresh() sets the isRefreshing lock so any API calls fired
        // before auth completes (e.g. eager useQuery hooks) are queued instead of
        // triggering a spurious forceLogout() on their 401 responses.
        //
        // E2E TESTING: If a pre-seeded access token exists in localStorage (stored by
        // the Playwright auth setup to avoid token rotation issues in parallel tests),
        // use it directly without calling /auth/refresh. This is only active when the
        // special key is present; in normal usage this path is never taken.
        const e2eAccessToken = localStorage.getItem('_e2e_access_token');
        if (e2eAccessToken) {
          try {
            const parts = e2eAccessToken.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              const nowSec = Math.floor(Date.now() / 1000);
              if (payload.exp && payload.exp - nowSec > 60) {
                setAccessToken(e2eAccessToken);
                setUser(withPlatformOwner(parsed));
                setIsLoading(false);
                return;
              }
            }
          } catch { /* ignore, fall through to normal refresh */ }
        }
        beginInitialRefresh();
        apiClient.post('/auth/refresh', {})
          .then((r) => {
            if (r.data?.accessToken) {
              setAccessToken(r.data.accessToken);
              resolveInitialRefresh(r.data.accessToken);
              setUser(withPlatformOwner(parsed));
              // Sync profile fields (tenantSlug, planFeatures) from /auth/me on every refresh
              apiClient.get('/auth/me').then((profileRes) => {
                const profile = profileRes.data;
                if (profile) {
                  const updated = {
                    ...parsed,
                    ...(profile.tenantSlug && { tenantSlug: profile.tenantSlug }),
                    planFeatures: Array.isArray(profile.planFeatures) ? profile.planFeatures : [],
                    ...(profile.planName !== undefined && { planName: profile.planName }),
                    // Always sync language — may be null for new users (triggers LocalePickerModal)
                    language: profile.language ?? null,
                  };
                  localStorage.setItem('user', JSON.stringify(updated));
                  setUser(withPlatformOwner(updated));
                }
              }).catch(() => {
                // profile sync failed — user will be set from cached data
              });
            } else {
              resolveInitialRefresh(null, new Error('No access token in refresh response'));
              localStorage.removeItem('user');
            }
          })
          .catch((error) => {
            console.warn('Token refresh failed, clearing stale data:', error);
            resolveInitialRefresh(null, error);
            // Refresh failed — clear stale data
            localStorage.removeItem('user');
          })
          .finally(() => setIsLoading(false));
        return; // setIsLoading(false) handled in .finally
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.clear();
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void | TwoFactorRequired> => {
    const res = await apiClient.post('/auth/login', { email, password });

    // AUTH-62-7: Handle 2FA challenge — redirect to 2FA page instead of storing undefined token
    if (res.data.requiresTwoFactor === true) {
      return { requiresTwoFactor: true, tempToken: res.data.tempToken } as TwoFactorRequired;
    }

    // SECURITY: Store accessToken in memory only (XSS protection — not in localStorage).
    // refreshToken is set as an HttpOnly cookie by the API response — no localStorage write needed.
    setAccessToken(res.data.accessToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(withPlatformOwner(res.data.user));
    router.push('/dashboard');
  }, [router]);

  const register = useCallback(async (data: Record<string, unknown>) => {
    const res = await apiClient.post('/auth/register', data);
    // SECURITY: Store accessToken in memory only (XSS protection — not in localStorage).
    // refreshToken is set as an HttpOnly cookie by the API response — no localStorage write needed.
    setAccessToken(res.data.accessToken);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(withPlatformOwner(res.data.user));
    router.push('/dashboard');
  }, [router]);

  const logout = useCallback(async () => {
    // AUTH-62-4: Call POST /auth/logout to revoke the refresh token server-side.
    // Cookie is sent automatically; no need to read refreshToken from localStorage.
    try {
      await apiClient.post('/auth/logout', {}); // browser sends HttpOnly cookie automatically
    } catch (error) {
      console.warn('Logout API call failed, clearing state anyway:', error);
      // Always clear local state even if the server call fails
    } finally {
      // SECURITY: Clear in-memory access token and all localStorage auth data on logout.
      setAccessToken(null);
      localStorage.removeItem('accessToken'); // legacy key — clear if it exists
      localStorage.removeItem('refreshToken'); // cleanup any leftover migration token
      localStorage.removeItem('user');
      setUser(null);
      router.push('/login');
    }
  }, [router]);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await apiClient.get('/auth/me');
      const profileData = res.data;
      const stored = localStorage.getItem('user');
      const existing = stored ? JSON.parse(stored) : {};
      const merged = { ...existing, ...profileData };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(withPlatformOwner(merged));
    } catch (error) {
      console.warn('Failed to refresh user profile:', error);
      // Silent fail — profile refresh is non-critical
    }
  }, []);

  // Fetch companies whenever the user logs in / is restored
  useEffect(() => {
    if (!user) {
      setCompanies([]);
      return;
    }
    apiClient.get('/companies?limit=100').then((r) => {
      // API returns { data: [...], total: N } or plain array
      const list: Array<{ id: string; name: string; isDefault?: boolean; is_default?: boolean; country?: unknown }> = Array.isArray(r.data) ? r.data : (r.data?.data ?? []);
      const opts: CompanyOption[] = list.map((c) => ({
        id: c.id,
        name: c.name,
        isDefault: !!(c.isDefault || c.is_default),
        country: typeof c.country === 'string' ? c.country : undefined,
      }));
      setCompanies(opts);
      const storedId = localStorage.getItem('activeCompanyId');
      const resolved = resolveActiveCompany(opts, storedId);
      if (resolved) {
        setActiveCompanyId(resolved);
        localStorage.setItem('activeCompanyId', resolved);
      }
    }).catch(() => {
      // Company fetch failed — non-critical feature, user can proceed without company selector
    });
    // user?.id is the correct dep — we only want to re-fetch companies when the
    // logged-in user changes (login/logout), not on every profile field update.
  }, [user?.id]);

  const queryClient = useQueryClient();

  const setActiveCompany = useCallback((companyId: string) => {
    setActiveCompanyId(companyId);
    localStorage.setItem('activeCompanyId', companyId);
    // Invalidate all cached queries so every page re-fetches data for the new
    // company immediately instead of serving stale data from the previous one.
    queryClient.invalidateQueries();
  }, [queryClient]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId) ?? null;
  const activeCompanyName = activeCompany?.name ?? null;
  const companyCountry = activeCompany?.country || 'RO';

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      logout,
      register,
      refreshProfile,
      companies,
      activeCompanyId,
      activeCompanyName,
      activeCompany,
      companyCountry,
      setActiveCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
